import React from 'react';
import { Box, Typography, Skeleton, Avatar } from '@mui/material';
import { adminTokens as T } from '../../../theme/adminTokens';

export interface ActivityItem {
  id: string;
  icon?: React.ReactNode;
  title: string;
  meta?: string;
  timestamp: string;
  tone?: 'neutral' | 'success' | 'warning' | 'error' | 'info';
  onClick?: () => void;
}

export interface ActivityFeedProps {
  title: string;
  items: ActivityItem[];
  loading?: boolean;
  emptyMessage?: string;
  maxHeight?: number;
}

const toneToColors = (tone: ActivityItem['tone']) => {
  switch (tone) {
    case 'success': return { bg: T.color.success.soft, text: T.color.success.text };
    case 'warning': return { bg: T.color.warning.soft, text: T.color.warning.text };
    case 'error':   return { bg: T.color.error.soft,   text: T.color.error.text };
    case 'info':    return { bg: T.color.info.soft,    text: T.color.info.text };
    default:        return { bg: T.color.brand[50],    text: T.color.brand[600] };
  }
};

const formatTimeAgo = (iso: string) => {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
};

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  title, items, loading, emptyMessage = 'No recent activity', maxHeight = 360,
}) => {
  return (
    <Box sx={{
      bgcolor: T.color.surface.raised,
      border: `1px solid ${T.color.surface.border}`,
      borderRadius: `${T.radius.lg}px`,
      p: `${T.space[5]}px`,
      boxShadow: T.shadow.sm,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Typography sx={{
        fontSize: T.type.size.lg,
        fontWeight: T.type.weight.semibold,
        color: T.color.text.primary,
        mb: `${T.space[4]}px`,
      }}>
        {title}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[3]}px` }}>
          {[0, 1, 2, 3].map(i => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[3]}px` }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="40%" />
              </Box>
            </Box>
          ))}
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: T.color.text.tertiary, fontSize: T.type.size.sm,
        }}>
          {emptyMessage}
        </Box>
      ) : (
        <Box sx={{
          flex: 1, overflowY: 'auto', maxHeight,
          display: 'flex', flexDirection: 'column', gap: `${T.space[3]}px`,
          mr: `-${T.space[2]}px`, pr: `${T.space[2]}px`,
        }}>
          {items.map(item => {
            const colors = toneToColors(item.tone);
            return (
              <Box
                key={item.id}
                onClick={item.onClick}
                role={item.onClick ? 'button' : undefined}
                tabIndex={item.onClick ? 0 : undefined}
                onKeyDown={item.onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); item.onClick!(); } } : undefined}
                sx={{
                  display: 'flex', alignItems: 'flex-start', gap: `${T.space[3]}px`,
                  p: `${T.space[2]}px`,
                  borderRadius: `${T.radius.md}px`,
                  cursor: item.onClick ? 'pointer' : 'default',
                  transition: `background-color ${T.motion.fast}`,
                  '&:hover': item.onClick ? { backgroundColor: T.color.surface.sunken } : {},
                  '&:focus-visible': { outline: 'none', boxShadow: `0 0 0 2px ${T.color.brand[200]}` },
                }}
              >
                <Avatar sx={{
                  width: 32, height: 32,
                  bgcolor: colors.bg, color: colors.text,
                  fontSize: T.type.size.sm,
                  '& > svg': { fontSize: 18 },
                }}>
                  {item.icon}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: T.type.size.sm,
                    fontWeight: T.type.weight.medium,
                    color: T.color.text.primary,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {item.title}
                  </Typography>
                  {item.meta && (
                    <Typography sx={{
                      fontSize: T.type.size.xs,
                      color: T.color.text.secondary,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {item.meta}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{
                  fontSize: T.type.size.xs,
                  color: T.color.text.tertiary,
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {formatTimeAgo(item.timestamp)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ActivityFeed;
